import { ToggleGroup, ToggleGroupItem } from "~/components/ui/toggle-group";
import { MultiSelectOption } from "~/components/MultiSelect";
import { cn } from "~/lib/utils";
import { ASSET_CLASS_IDS } from "~/consts";
import { FilterLabel } from "../FilterLabel";
import {
    ASSET_CLASS_CATEGORY_ITEM_CLASS,
    FILTER_TOGGLE_ITEM_CLASS,
} from "./styles";

// // // //

// Asset class groups
// Copied from ~/modules/asset_screener/pages/listing/assetClassGroups.ts

interface AssetClassGroupDefinition {
    id: string;
    label: string;
    memberIds: number[];
}

const ASSET_CLASS_GROUP_DEFINITIONS: AssetClassGroupDefinition[] = [
    {
        id: "equities",
        label: "Equities",
        memberIds: [
            ASSET_CLASS_IDS.Stocks,
            ASSET_CLASS_IDS.PrivateEquity,
            ASSET_CLASS_IDS.VentureCapital,
        ],
    },
    {
        id: "alternatives",
        label: "Alternatives",
        memberIds: [ASSET_CLASS_IDS.ActiveStrategies],
    },
    {
        id: "credit",
        label: "Credit",
        memberIds: [
            ASSET_CLASS_IDS.CorporateCredit,
            ASSET_CLASS_IDS.DiversifiedCredit,
            ASSET_CLASS_IDS.AssetBackedCredit,
            ASSET_CLASS_IDS.SpecialtyFinance,
            ASSET_CLASS_IDS.PrivateCredit,
            ASSET_CLASS_IDS.MunicipalBonds,
            ASSET_CLASS_IDS.RepurchaseAgreements,
        ],
    },
    {
        id: "rates",
        label: "Rates",
        memberIds: [
            ASSET_CLASS_IDS.TreasuryProduct,
            ASSET_CLASS_IDS.NonUSGovernmentDebt,
        ],
    },
    {
        id: "commodities",
        label: "Commodities",
        memberIds: [ASSET_CLASS_IDS.Commodities],
    },
    {
        id: "real-estate",
        label: "Real Estate",
        memberIds: [ASSET_CLASS_IDS.RealEstate],
    },
];

interface AssetClassGroup {
    id: string;
    label: string;
    members: MultiSelectOption[];
}

function buildAssetClassGroups(
    assetClassOptions: MultiSelectOption[],
): AssetClassGroup[] {
    const optionsByValue = new Map(
        assetClassOptions.map((option) => [option.value, option]),
    );
    const groupedOptionValues = new Set<string>();

    const groups = ASSET_CLASS_GROUP_DEFINITIONS.map((group) => {
        const members = group.memberIds
            .map((memberId) => optionsByValue.get(String(memberId)))
            .filter((option): option is MultiSelectOption => option != null);

        members.forEach((member) => groupedOptionValues.add(member.value));

        return {
            id: group.id,
            label: group.label,
            members,
        };
    }).filter((group) => group.members.length > 0);

    const ungroupedMembers = assetClassOptions.filter(
        (option) => !groupedOptionValues.has(option.value),
    );

    if (ungroupedMembers.length > 0) {
        groups.push({
            id: "other",
            label: "Other",
            members: ungroupedMembers,
        });
    }

    return groups;
}

// // // //

/**
 * AssetClassFilter
 * Asset class picker with two rows: category toggles (Equities, Credit, ...)
 * that select/deselect every member class, and per-class toggle chips.
 * Copied from ~/modules/asset_screener/pages/listing/AssetClassFilter.tsx
 * Count badges only render when the options have counts (see assetClassCounts)
 */
export function AssetClassFilter(props: {
    selectedAssetClasses: string[];
    onAssetClassesChange: (value: string[]) => void;
    assetClassOptions: MultiSelectOption[];
}) {
    const { selectedAssetClasses, onAssetClassesChange, assetClassOptions } =
        props;

    const groups = buildAssetClassGroups(assetClassOptions);
    const selectedAssetClassSet = new Set(selectedAssetClasses);
    const selectedCategoryIds = groups
        .filter((group) =>
            group.members.every((option) =>
                selectedAssetClassSet.has(option.value),
            ),
        )
        .map((group) => group.id);
    const selectedCategoryIdSet = new Set(selectedCategoryIds);

    const handleCategoryToggle = (group: (typeof groups)[number]) => {
        const nextAssetClassSet = new Set(selectedAssetClasses);
        const isSelected = selectedCategoryIdSet.has(group.id);

        if (isSelected) {
            group.members.forEach((option) =>
                nextAssetClassSet.delete(option.value),
            );
        } else {
            group.members.forEach((option) =>
                nextAssetClassSet.add(option.value),
            );
        }

        onAssetClassesChange(
            assetClassOptions
                .map((option) => option.value)
                .filter((value) => nextAssetClassSet.has(value)),
        );
    };

    return (
        <div className="space-y-3">
            <div className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <FilterLabel label="Asset class category" />
                        {selectedAssetClasses.length > 0 && (
                            <button
                                type="button"
                                onClick={() => onAssetClassesChange([])}
                                className="inline-flex cursor-pointer items-center rounded-sm px-1.5 py-0.5 text-xs leading-none font-medium text-blue-600 transition-colors hover:bg-primary-foreground hover:text-blue-700"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                <div
                    className="inline-flex w-fit max-w-full flex-wrap items-center gap-1 rounded-lg border border-border/70 bg-tertiary p-1"
                    aria-label="Asset class categories"
                    role="toolbar"
                >
                    {groups.map((group) => {
                        const selectedMemberCount = group.members.filter(
                            (option) => selectedAssetClassSet.has(option.value),
                        ).length;
                        const isSelected = selectedCategoryIdSet.has(group.id);
                        const isPartiallySelected =
                            selectedMemberCount > 0 &&
                            selectedMemberCount < group.members.length;
                        const hasCounts = group.members.some(
                            (option) => typeof option.count === "number",
                        );
                        const totalCount = group.members.reduce(
                            (sum, option) => sum + (option.count ?? 0),
                            0,
                        );

                        return (
                            <button
                                key={group.id}
                                type="button"
                                aria-pressed={isSelected}
                                className={cn(
                                    ASSET_CLASS_CATEGORY_ITEM_CLASS,
                                    "whitespace-nowrap",
                                    {
                                        "bg-card text-blue-700 shadow-sm ring-1 ring-blue-300":
                                            isSelected,
                                        "bg-primary-foreground/60 text-blue-700 ring-1 ring-blue-200":
                                            isPartiallySelected && !isSelected,
                                        "text-secondary-foreground hover:bg-card/70 hover:text-foreground":
                                            !isSelected && !isPartiallySelected,
                                    },
                                )}
                                onClick={() => handleCategoryToggle(group)}
                            >
                                <span>{group.label}</span>
                                {hasCounts && (
                                    <span
                                        className={cn(
                                            "rounded-full px-1.5 py-0.5 text-[11px] leading-none font-medium tabular-nums",
                                            {
                                                "bg-primary-light text-blue-700":
                                                    isSelected ||
                                                    isPartiallySelected,
                                                "bg-secondary/60 text-secondary-foreground":
                                                    !isSelected &&
                                                    !isPartiallySelected,
                                            },
                                        )}
                                    >
                                        {totalCount}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="space-y-1">
                <FilterLabel label="Asset class" />
                <ToggleGroup
                    type="multiple"
                    value={selectedAssetClasses}
                    onValueChange={onAssetClassesChange}
                    className="flex w-full flex-wrap items-center justify-start gap-x-1 gap-y-1.5"
                    aria-label="Asset classes"
                >
                    {groups.map((group) => (
                        <div key={group.id} className="contents">
                            {group.members.map((option) => (
                                <ToggleGroupItem
                                    key={option.value}
                                    value={option.value}
                                    size="sm"
                                    className={`${FILTER_TOGGLE_ITEM_CLASS} group`}
                                >
                                    <span>{option.label}</span>
                                    {typeof option.count === "number" && (
                                        <span className="rounded-full bg-secondary/50 px-1.5 py-0.5 text-[11px] leading-none text-secondary-foreground group-data-[state=on]:bg-primary-light group-data-[state=on]:text-blue-700">
                                            {option.count}
                                        </span>
                                    )}
                                </ToggleGroupItem>
                            ))}
                        </div>
                    ))}
                </ToggleGroup>
            </div>
        </div>
    );
}
